import { userToProjectUserRelationFactory } from 'factories/projects';
import { saveProjectDraftObject } from 'redux/modules/projects.actions';
import { getTagEntities } from 'redux/modules/tags.selectors';
import {
  getAccountCurrency,
  getIsSubscriptionExpiredOrTrialEnded,
  getRecurringBudgetsEnabled,
} from 'redux/selectors/accounts';
import { getActiveClients } from 'redux/selectors/clients';
import { getHasReachedProjectLimit } from 'redux/selectors/projects';
import { getUser, getUserPermissions } from 'redux/selectors/user';
import type { Thunk } from 'types/redux';
import { generateProjectDraft, mapDefaultTagsForDraft } from 'utils/project';

/** Use the normal project defaults and save pipeline without opening a global draft. */
export function createAgentProject(
  name: string,
  clientId: number,
  color: string | null = null
): Thunk<Promise<number>> {
  return async (dispatch, getState) => {
    const state = getState();
    if (!getUserPermissions(state).canCreate.projects)
      throw new Error('You don’t have permission to create projects.');
    if (getIsSubscriptionExpiredOrTrialEnded(state))
      throw new Error('Your subscription must be active to create a project.');
    if (getHasReachedProjectLimit(state))
      throw new Error('Your workspace has reached its project limit.');
    const client = getActiveClients(state).find(c => c.id === clientId);
    if (!client) throw new Error('Select an active client.');
    if (!name.trim()) throw new Error('Enter a project name.');
    const draft = generateProjectDraft(
      {
        name: name.trim(),
        color,
        client,
        currency: getAccountCurrency(state),
        users: [userToProjectUserRelationFactory(getUser(state))],
      },
      { recurringBudgetsAvailable: getRecurringBudgetsEnabled(state) }
    );
    mapDefaultTagsForDraft(draft, getTagEntities(state));
    const project = await dispatch(
      saveProjectDraftObject(draft, {
        useClientColor: color === null,
        refetchProjectList: true,
        showErrorToast: false,
      })
    );
    return project.id;
  };
}
